import { AxiosError } from 'axios'
import { useCallback } from 'react'

import { useQuery, UseQueryOptions } from '@tanstack/react-query'

import APIServicesV2 from '../../api/APIServicesV2'
import { Container } from '@/interfaces/container'

export interface ContainerQueryParams {
  per_page?: number | null
  page?: number | null
  status?: string | null
  operationNumber?: string | null | number
  name?: string | null
  search?: string | null
}

export interface ContainerResponse {
  totalItems: number
  currentPage: number
  totalPages: number
  items: Container[]
}

// Create a unique, descriptive key for this query
const CONTAINER_QUERY_KEY = 'containers'

/**
 * Fetch containers from the API
 * Separating API call logic from the hook for better testability
 */
const fetchContainers = async (
  params?: ContainerQueryParams
): Promise<ContainerResponse> => {
  // Filter out null/undefined values to clean up the request
  const cleanParams = Object.fromEntries(
    Object.entries(params || {}).filter(([_, value]) => value != null)
  )

  const response = await APIServicesV2.get('/containers', {
    params: cleanParams,
  })
  return response.data
}

/**
 * Custom hook for fetching containers with filtering
 * This hook now properly uses external parameters without internal state conflicts
 *
 * @param params - Query parameters for filtering containers
 * @param options - TanStack Query options for customizing query behavior
 * @returns Query result object containing data, loading states, and utility functions
 */
export const useContainerQuery = ({
  params = {},
  options,
  businessId,
}: {
  params?: ContainerQueryParams
  options?: Partial<UseQueryOptions<ContainerResponse, AxiosError>>
  businessId: number
}) => {
  // Normalize params with defaults
  const normalizedParams: ContainerQueryParams = {
    page: 1,
    per_page: 10,
    ...params,
  }

  // Query hook - now properly reactive to external parameter changes
  const queryResult = useQuery<ContainerResponse, AxiosError>({
    // Create a structured query key that includes all parameters
    // This ensures the query refetches when parameters change
    queryKey: [CONTAINER_QUERY_KEY, normalizedParams, businessId],

    // Separate the API call logic from the query configuration
    queryFn: () => fetchContainers(normalizedParams),

    // Default options that can be overridden
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes

    // Apply any custom options
    ...options,
  })

  // Helper functions for pagination metadata
  const getCurrentPage = useCallback(() => {
    return normalizedParams.page || 1
  }, [normalizedParams.page])

  const getPageSize = useCallback(() => {
    return normalizedParams.per_page || 10
  }, [normalizedParams.per_page])

  const hasNextPage = useCallback(() => {
    if (!queryResult.data) return false
    const { currentPage, totalPages } = queryResult.data
    return currentPage < totalPages
  }, [queryResult.data])

  const hasPreviousPage = useCallback(() => {
    const currentPage = getCurrentPage()
    return currentPage > 1
  }, [getCurrentPage])

  return {
    ...queryResult,
    // Add pagination helpers and metadata
    pagination: {
      currentPage: getCurrentPage(),
      pageSize: getPageSize(),
      hasNextPage: hasNextPage(),
      hasPreviousPage: hasPreviousPage(),
      totalPages: queryResult.data?.totalPages || 0,
      totalItems: queryResult.data?.totalItems || 0,
      params: normalizedParams,
    },
  }
}

/**
 * Helper function to get the container query key
 * Useful for cache invalidation or prefetching
 */
export const getContainersQueryKey = (params?: ContainerQueryParams) => [
  CONTAINER_QUERY_KEY,
  params,
]
